const DEVELOPMENT_SERVER_COMMANDS = new Set([
  "dev",
  "serve",
  "development",
  "development-server",
]);

const SAFE_RELEASE_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;

export const DEPLOYMENT_PROFILES = Object.freeze({
  LEGACY_APP: "legacy-app",
  WWW_CANDIDATE: "www-candidate",
});

export const DEPLOYMENT_ASSET_OWNERS = Object.freeze({
  PRODUCT: "product",
  MARKETING: "marketing",
});

function normalizedCommand(command) {
  if (typeof command !== "string" || command.trim() === "") {
    throw new Error("deployment command is required");
  }
  return command.trim().toLowerCase();
}

function normalizedProfile(profile) {
  if (profile === undefined || profile === null) return undefined;
  if (typeof profile !== "string") throw new Error("deployment profile must be a string");
  const value = profile.trim();
  return value === "" ? undefined : value;
}

function hasReleaseId(releaseId) {
  return releaseId !== undefined && releaseId !== null;
}

function normalizedAssetOwner(assetOwner) {
  const owner = assetOwner ?? DEPLOYMENT_ASSET_OWNERS.PRODUCT;
  if (!Object.values(DEPLOYMENT_ASSET_OWNERS).includes(owner)) {
    throw new Error(`unknown deployment asset owner: ${String(owner)}`);
  }
  return owner;
}

function candidateResult(releaseId, assetOwner) {
  if (typeof releaseId !== "string" || !SAFE_RELEASE_ID.test(releaseId)) {
    throw new Error("www-candidate requires a valid release id");
  }
  return {
    profile: DEPLOYMENT_PROFILES.WWW_CANDIDATE,
    releaseId,
    assetBase: `/__static/${assetOwner}/${releaseId}/`,
  };
}

/**
 * Resolve the one deployment-profile contract shared by build-time callers.
 * Development servers may use the legacy app defaults; all release-producing
 * commands must identify their profile explicitly.
 */
export function resolveDeploymentProfile({ command, profile, releaseId, assetOwner } = {}) {
  const commandName = normalizedCommand(command);
  const selectedProfile = normalizedProfile(profile);
  const selectedAssetOwner = normalizedAssetOwner(assetOwner);
  const releaseWasSupplied = hasReleaseId(releaseId);
  const isDevelopmentServer = DEVELOPMENT_SERVER_COMMANDS.has(commandName);

  if (selectedProfile === undefined) {
    if (releaseWasSupplied) {
      throw new Error("a release id requires an explicit deployment profile");
    }
    if (!isDevelopmentServer) {
      throw new Error(`deployment profile is required for ${commandName}`);
    }
    return {
      profile: DEPLOYMENT_PROFILES.LEGACY_APP,
      releaseId: undefined,
      assetBase: "/",
    };
  }

  if (selectedProfile === DEPLOYMENT_PROFILES.LEGACY_APP) {
    if (releaseWasSupplied) {
      throw new Error("legacy-app cannot be used with a release id");
    }
    return {
      profile: DEPLOYMENT_PROFILES.LEGACY_APP,
      releaseId: undefined,
      assetBase: "/",
    };
  }

  if (selectedProfile === DEPLOYMENT_PROFILES.WWW_CANDIDATE) {
    return candidateResult(releaseId, selectedAssetOwner);
  }

  throw new Error(`unknown deployment profile: ${selectedProfile}`);
}
